import { useEffect, useRef, useState } from "react";
import { listGrantViewers } from "../../lib/accessClient.js";
import { ACTIVITY_CAPABILITIES, RAW_PROMPT_CAPABILITY, listViewerActivityGrants } from "../../lib/activityConfiguration.js";
import { hasSensitivePermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { roleLabel } from "../../lib/userRoles.js";
import { recipientName, scopeName } from "./ActivityConfigurationFields.jsx";
import OverlayDialog from "../OverlayDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

const NOTE_CLASS = "text-[11px] leading-[1.5] text-[color:var(--muted)]";
const EYEBROW_CLASS = "text-[11px] font-medium text-[color:var(--muted)]";
const PAGE_SIZE = 25;
const EMPTY_PAGE = { grants: [], nextPageToken: "", inheritanceStatus: "", enforcementEnabled: null, checkedAt: 0 };
const buttonClass = "min-h-[36px] rounded-[4px] border border-[color:var(--line)] px-[10px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 cursor-pointer";

function UserScopeDetails({ viewer, membershipRole, onOpenGrant }) {
    const request = useRef(0);
    const [page, setPage] = useState({ ...EMPTY_PAGE, loading: true, error: "" });
    const load = async (token = "") => {
        const current = ++request.current;
        setPage(previous => ({ ...(token ? previous : EMPTY_PAGE), loading: true, error: "" }));
        try {
            const result = await listViewerActivityGrants({ viewerUserId: viewer.viewerUserId, pageSize: PAGE_SIZE, pageToken: token });
            if (current !== request.current) return;
            setPage(previous => ({ ...result, loading: false, error: "", checkedAt: Date.now(),
                grants: token ? [...previous.grants, ...result.grants] : result.grants }));
        } catch (error) {
            if (current !== request.current) return;
            if (token && ["INVALID_ARGUMENT", "ABORTED"].includes(error?.grpcCode)) {
                setPage({ ...EMPTY_PAGE, loading: false, error: "Grant details changed while loading. Retry to load the current grants." });
            } else {
                setPage(previous => ({ ...previous, loading: false, error: "Grant details are unavailable right now. Try again later." }));
            }
        }
    };
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        loadRef.current();
        return () => { request.current += 1; };
    }, [viewer.viewerUserId]);
    useEffect(() => {
        const expiries = page.grants.map(row => Number(row.grant?.expiresAtUnixMs)).filter(value => value > 0);
        if (!expiries.length || page.loading || page.error) return undefined;
        const timer = setTimeout(() => loadRef.current(), Math.min(2147483647, Math.max(1000, Math.min(...expiries) - Date.now())));
        return () => clearTimeout(timer);
    }, [page.grants, page.loading, page.error]);
    const enforcementKnown = typeof page.enforcementEnabled === "boolean";
    const inheritedKnown = page.inheritanceStatus === "resolved";
    return <section aria-label="Selected user access" className="grid min-w-0 content-start gap-[22px] py-[6px]">
        <header className="flex flex-wrap items-start justify-between gap-[10px]">
            <div className="grid min-w-0 gap-[4px]">
                <span className={EYEBROW_CLASS}>Viewing access for</span>
                <h3 className="m-0 break-words text-[16px] font-medium text-[color:var(--ink)]">{viewer.displayName || "Selected user"}</h3>
            </div>
            <button type="button" className={buttonClass} disabled={page.loading} onClick={() => load()}>Refresh access</button>
        </header>
        {enforcementKnown ? <div className="grid gap-[8px] border-l-[3px] border-[color:var(--green)] pl-[16px] py-[4px]">
            <strong className="text-[16px] font-medium text-[color:var(--ink)]">{page.enforcementEnabled ? "Activity grants enforced" : "ABAC is off"}</strong>
            <p className={`m-0 ${NOTE_CLASS}`}>{page.enforcementEnabled
                ? "Only capability grants provide activity access. Tenant Admin, SecOps, and Detection Engineer roles do not bypass activity scopes."
                : "Existing role-based activity access remains unchanged. These prepared capability grants will apply when ABAC is enabled; they do not describe this person's current legacy access."}</p>
            {page.checkedAt ? <span className={NOTE_CLASS}>Checked {new Date(page.checkedAt).toLocaleString()}.</span> : null}
        </div> : !page.loading && !page.error ? <p className={NOTE_CLASS}>The tenant's enforcement setting could not be confirmed. These grants do not establish current activity access.</p> : null}
        <div className="grid gap-[6px]">
            <span className={EYEBROW_CLASS}>Operational role</span>
            <span className="text-[11px] text-[color:var(--ink)]">{membershipRole ? roleLabel(membershipRole) : "Role details unavailable"}</span>
            <p className={`m-0 ${NOTE_CLASS}`}>User and role administration is managed separately from these capability grants.</p>
        </div>
        <section aria-label="Configured grants" aria-busy={page.loading} className="grid gap-[14px] border-t border-[color:var(--line-soft)] pt-[14px]">
            <h3 className="m-0 text-[14px] font-medium text-[color:var(--ink)]">{page.enforcementEnabled === false ? "Prepared capability grants" : "Capability grants"}</h3>
            {page.inheritanceStatus === "viewer_unmapped" ? <p className={`m-0 ${NOTE_CLASS}`}>We could not confirm this person's group access. Any direct grants are shown below.</p> : null}
            {page.inheritanceStatus === "no_active_generation" ? <p className={`m-0 ${NOTE_CLASS}`}>Group access details are unavailable right now. Any direct grants are shown below.</p> : null}
            {page.inheritanceStatus === "unknown" ? <p className={`m-0 ${NOTE_CLASS}`}>Group access has not been confirmed.</p> : null}
            {ACTIVITY_CAPABILITIES.map(capability => {
                const rows = page.grants.filter(row => row.grant?.capability === capability.value);
                return <div key={capability.value} aria-label={capability.label} className="grid gap-[8px]">
                    <h4 className="m-0 text-[13px] font-medium text-[color:var(--ink)]">{capability.label}</h4>
                    <p className={`m-0 ${NOTE_CLASS}`}>{capability.description}</p>
                    {rows.map(row => <div key={row.id} className="grid gap-[4px] border-l border-[color:var(--line-soft)] pl-[12px] text-[12px] text-[color:var(--ink)]">
                        <span>{row.recipient?.directoryGroup ? <>Through group: <strong>{recipientName(row.recipient)}</strong></> : "Assigned directly"}</span>
                        <strong>{scopeName(row.grant.scope)}</strong>
                        {Number(row.grant.expiresAtUnixMs) > 0 ? <span className={NOTE_CLASS}>Expires {new Date(Number(row.grant.expiresAtUnixMs)).toLocaleString()}.</span> : null}
                        {onOpenGrant ? <button type="button" className="justify-self-start underline underline-offset-4 cursor-pointer" onClick={() => onOpenGrant(row)}>View grant</button> : null}
                    </div>)}
                    {!rows.length && !page.loading && !page.error ? <p className={`m-0 ${NOTE_CLASS}`}>{page.nextPageToken ? "No grants of this type on the loaded pages." : inheritedKnown ? "No grants of this type." : "No direct grants of this type. Group access has not been confirmed."}</p> : null}
                    {capability.value !== RAW_PROMPT_CAPABILITY ? <p className={`m-0 ${NOTE_CLASS}`}>Prompt and response grants also include metadata within their own scopes.</p> : null}
                </div>;
            })}
            {page.loading ? <span role="status" className={NOTE_CLASS}>Loading grants...</span> : null}
            {page.error ? <div role="alert" className="grid gap-[8px]"><span className={NOTE_CLASS}>{page.error}</span><button type="button" className={buttonClass} onClick={() => load(page.nextPageToken)}>Retry grants</button></div> : null}
            {!page.loading && !page.error && !page.grants.length && inheritedKnown && page.enforcementEnabled === true ? <p className={`m-0 ${NOTE_CLASS}`}>No activity access. This person has no metadata or prompt grants.</p> : null}
            {page.grants.length ? <span className={NOTE_CLASS}>Showing {page.grants.length} {page.grants.length === 1 ? "grant" : "grants"}{page.nextPageToken ? " so far" : ""}.</span> : null}
            {page.nextPageToken && !page.error ? <button type="button" className={buttonClass} disabled={page.loading} onClick={() => load(page.nextPageToken)}>Load more grants</button> : null}
        </section>
    </section>;
}

export default function UserEffectiveAccessDialog({ user, onClose, onOpenGrant }) {
    const contentRef = useRef(null);
    const caps = useMyPermissions();
    const allowed = hasSensitivePermission(caps, PERMISSIONS.GRANTS_READ)
        && hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    useEffect(() => {
        const previouslyFocused = document.activeElement;
        const dialog = contentRef.current?.closest('[role="dialog"]');
        const focusable = () => [...(dialog?.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), summary, [tabindex="0"]') ?? [])];
        focusable()[0]?.focus();
        const trapFocus = (event) => {
            if (event.key !== "Tab") return;
            const items = focusable();
            const first = items[0];
            const last = items.at(-1);
            if (event.shiftKey && (document.activeElement === first || !dialog?.contains(document.activeElement))) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !dialog?.contains(document.activeElement))) {
                event.preventDefault();
                first?.focus();
            }
        };
        dialog?.addEventListener("keydown", trapFocus);
        return () => {
            dialog?.removeEventListener("keydown", trapFocus);
            if (previouslyFocused?.isConnected) previouslyFocused.focus();
        };
    }, []);
    const [retry, setRetry] = useState(0);
    const [lookup, setLookup] = useState({ loading: true, viewer: null, error: "" });
    useEffect(() => {
        if (!allowed) return undefined;
        let cancelled = false;
        setLookup({ loading: true, viewer: null, error: "" });
        const email = user.email?.trim();
        if (!email) {
            setLookup({ loading: false, viewer: null, error: "Add an email address to this user to check their activity access." });
            return undefined;
        }
        listGrantViewers({ pageSize: PAGE_SIZE, search: email }).then((page) => {
            if (cancelled) return;
            const matches = page.viewers.filter((viewer) => viewer.displayName.trim().toLowerCase() === email.toLowerCase());
            if (matches.length !== 1 || !matches[0]?.viewerUserId || page.nextPageToken || page.totalCount > page.viewers.length) {
                setLookup({ loading: false, viewer: null, error: matches.length > 1 || page.nextPageToken || page.totalCount > page.viewers.length
                    ? "We could not confirm this person's account. Check their email address and try again."
                    : "Activity access is not available for this person yet." });
                return;
            }
            setLookup({ loading: false, viewer: matches[0], error: "" });
        }, () => {
            if (!cancelled) setLookup({ loading: false, viewer: null, error: "We couldn't check this user's activity access. Try again in a moment." });
        });
        return () => { cancelled = true; };
    }, [allowed, retry, user.userId, user.email]);
    return (
        <OverlayDialog open onClose={onClose} title="Activity access" width="720px" analyticsId={DIALOG.ACTIVITY_ACCESS}>
            <div ref={contentRef}>
            {!allowed ? <p className={NOTE_CLASS}>You do not have permission to view this person's activity access.</p> : (
                <div aria-busy={lookup.loading}>
                    {lookup.loading ? <p role="status" className={NOTE_CLASS}>Checking activity access...</p> : lookup.error ? (
                        <div role="alert" className="grid gap-[12px] text-[11px] text-[color:var(--ink)]">
                            <span>{lookup.error}</span>
                            <button type="button" className={buttonClass} onClick={() => setRetry((current) => current + 1)}>Try again</button>
                        </div>
                    ) : <UserScopeDetails key={lookup.viewer.viewerUserId} viewer={lookup.viewer} membershipRole={user.roleSlug} onOpenGrant={onOpenGrant} />}
                </div>
            )}
            </div>
        </OverlayDialog>
    );
}
